import React, { useMemo } from 'react';
import { MatrixEvent } from 'matrix-js-sdk';
import { matrixService } from '../../services/matrixService';
import { isServiceParticipant } from '../../utils/aiBotUtils';

interface SystemMessageProps {
    message: MatrixEvent;
    roomId: string;
    getClient: () => any;
}

const SystemMessage: React.FC<SystemMessageProps> = ({ message, roomId, getClient }) => {
    const systemMessageText = useMemo(() => {
        const eventType = message.getType();
        const content = message.getContent();
        const sender = message.getSender();
        const client = getClient();
        const room = client?.getRoom(roomId);

        if (!room || !sender) return null;

        const senderMember = room.getMember(sender);
        const senderName = senderMember?.rawDisplayName || sender;

        switch (eventType) {
            case 'm.room.name': {
                const newName = content.name;
                const prevContent = message.getPrevContent();
                const oldName = prevContent?.name;

                if (oldName && newName) {
                    return `${senderName} changed group name from "${oldName}" to "${newName}"`;
                } else if (newName) {
                    return `${senderName} changed group name to "${newName}"`;
                } else {
                    return `${senderName} removed the room name`;
                }
            }

            case 'm.room.member': {
                const membership = content.membership;
                const prevMembership = message.getPrevContent()?.membership;

                // The user this event is about
                const targetUserId = message.getStateKey();
                const targetMember = room.getMember(targetUserId || '');
                const targetName = targetMember?.rawDisplayName || targetUserId;

                // Filter out initial member events from room creation
                const roomCreateEvent = room.currentState.getStateEvents('m.room.create', '');
                if (roomCreateEvent) {
                    const roomCreateTs = roomCreateEvent.getTs();
                    const memberEventTs = message.getTs();
                    const timeDiff = memberEventTs - roomCreateTs;

                    // If member event happened within 30 seconds of room creation, it's an initial member
                    if (timeDiff >= 0 && timeDiff < 30000) {
                        return null;
                    }
                }

                // --- This is the part you were asking about ---
                // The user ID of the person who sent the *previous* event (e.g., the invite)
                const invitedByUserId = message?.event.unsigned?.prev_sender;
                const inviterMember = invitedByUserId ? room.getMember(invitedByUserId) : null;
                const inviterName = inviterMember?.rawDisplayName || invitedByUserId;

                // A direct chat is not a group, and calling it one is what made
                // us mistake a DM for a two-person group in the first place.
                const isDirect = matrixService.isDirectMessageRoom(room);
                const place = isDirect ? 'this chat' : 'this group';
                // Only a human participant joining starts a direct chat.
                const startsDirectChat = isDirect && !isServiceParticipant(targetUserId || '');

                // User joined
                if (membership === 'join') {
                    if (prevMembership === 'invite') {
                        return startsDirectChat
                            ? `${inviterName} started this chat`
                            : `${inviterName} added ${targetName} to ${place}`;
                    } else if (prevMembership !== 'join') {
                        if (sender === targetUserId) {
                            return `${targetName} joined ${place}`;
                        } else {
                            return startsDirectChat
                                ? `${senderName} started this chat`
                                : `${senderName} added ${targetName} to ${place}`;
                        }
                    }
                }

                if (membership === 'leave') {
                    if (sender === targetUserId) {
                        return `${senderName} left ${place}`;
                    } else {
                        return `${senderName} removed ${targetName} from ${place}`;
                    }
                }

                if (membership === 'ban') {
                    const reason = content.reason;
                    if (reason) {
                        return `${senderName} banned ${targetName} (${reason})`;
                    }
                    return `${senderName} banned ${targetName}`;
                }

                if (membership === 'join' && prevMembership === 'join') {
                    const newDisplayName = content.displayname;
                    const oldDisplayName = message.getPrevContent()?.displayname;

                    if (newDisplayName !== oldDisplayName && newDisplayName && oldDisplayName) {
                        return `${oldDisplayName} changed their display name to ${newDisplayName}`;
                    }
                }

                return null;
            }

            default:
                return null;
        }
    }, [message, roomId, getClient]);

    if (!systemMessageText) return null;

    return (
        <div className="flex justify-center my-4">
            <div className="bg-canvas text-ink-muted-strong px-4 py-2.5 rounded-full text-tiny font-medium max-w-[80%] text-center">
                {systemMessageText}
            </div>
        </div>
    );
};

export default SystemMessage;
